import cardValidator from 'card-validator';
import { CardNumberVerification } from 'card-validator/dist/card-number';
import { getVisibleElementById } from 'src/utils/dom';
import { defaultCardParameters } from 'src/utils/validations';

export class CardService {
  cardFormData = {
    cvv: '',
    number: '',
    cardType: '',
    expirationYear: '',
    expirationMonth: '',
  };

  private prettyCardNumber = (
    cardNumber: string,
    card?: CardNumberVerification['card'],
  ) => {
    const cardParams = card || defaultCardParameters;

    const components = [];
    const offsets = [0, ...cardParams.gaps, cardNumber.length];

    for (let i = 0; offsets[i] < cardNumber.length; i++) {
      const start = offsets[i];
      const end = Math.min(offsets[i + 1], cardNumber.length);
      components.push(cardNumber.substring(start, end));
    }
    return components.join(' ');
  };

  private prettyExpiryDate = (newValue: string, oldValue: string) => {
    const regex = /^[2-9]$/;

    if (oldValue && oldValue.length > newValue.length) {
      return newValue;
    }

    if (String(newValue).length === 1 && regex.test(newValue)) {
      // eslint-disable-next-line no-param-reassign
      newValue = `0${newValue}`;
    }

    return newValue
      .replace(/\D/g, '')
      .replace(/\W/gi, '')
      .replace(/(.{2})/g, '$1/')
      .replace('00', '01')
      .substring(0, 5);
  };

  initCardNumberFormatter = (id: string, cvvId: string, validate?: boolean) => {
    const input = getVisibleElementById<'input'>(id) as HTMLInputElement;
    const cvv = getVisibleElementById<'input'>(cvvId) as HTMLInputElement;
    input.addEventListener('input', () => {
      this.cardFormData.number = input.value.replace(/\D/g, '');
      const { card, isPotentiallyValid } = cardValidator.number(
        this.cardFormData.number,
      );
      input.value = this.prettyCardNumber(this.cardFormData.number, card);
      this.cardFormData.cardType = card?.type || '';

      // Setting cvv maxlength
      if (cvv && card?.code?.size) {
        const maxlength = card.code.size.toString();
        cvv.setAttribute('maxlength', maxlength);
      }

      // With validation
      if (validate) {
        if (!isPotentiallyValid) {
          input.classList.add('error');
        } else {
          input.classList.remove('error');
        }

        if (window.DEBUG)
          console.log(
            'initCardNumberFormatter',
            cardValidator.number(this.cardFormData.number),
          );
      }
    });
    if (validate) {
      input.addEventListener('blur', () => {
        const { isValid } = cardValidator.number(this.cardFormData.number);
        if (!isValid) {
          input.classList.add('error');
        } else {
          input.classList.remove('error');
        }
      });
    }
  };

  initCvvFormatter = (id: string, validate?: boolean) => {
    const input = getVisibleElementById<'input'>(id) as HTMLInputElement;
    input.addEventListener('input', () => {
      this.cardFormData.cvv = input.value;

      if (validate) {
        const { isPotentiallyValid } = cardValidator.cvv(input.value);
        if (!isPotentiallyValid) {
          input.classList.add('error');
        } else {
          input.classList.remove('error');
        }

        if (window.DEBUG) {
          console.log('initCvvFormatter', cardValidator.cvv(input.value));
        }
      }
    });
    if (validate) {
      input.addEventListener('blur', () => {
        const { isValid } = cardValidator.cvv(input.value);
        if (!isValid) {
          input.classList.add('error');
        } else {
          input.classList.remove('error');
        }
      });
    }
  };

  initExpiryFormatter = (id: string, validate?: boolean) => {
    const input = getVisibleElementById<'input'>(id) as HTMLInputElement;
    let oldValue = input.value;

    input.addEventListener('input', () => {
      const newValue = this.prettyExpiryDate(input.value, oldValue);
      input.value = newValue;
      oldValue = newValue;
      const { month, year, isPotentiallyValid } = cardValidator.expirationDate(
        input.value,
      );
      this.cardFormData.expirationMonth = month || '';
      this.cardFormData.expirationYear = year || '';

      if (validate) {
        if (!isPotentiallyValid) {
          input.classList.add('error');
        } else {
          input.classList.remove('error');
        }

        if (window.DEBUG) {
          console.log(
            'initExpiryFormatter',
            cardValidator.expirationDate(input.value),
          );
        }
      }
    });
    if (validate) {
      input.addEventListener('blur', () => {
        const { isValid } = cardValidator.expirationDate(input.value);
        if (!isValid) {
          input.classList.add('error');
        } else {
          input.classList.remove('error');
        }
      });
    }
  };
}
